/**
 * Client HTTP vers le backend de paiement (backend/payment-server.js).
 * Ajoute automatiquement le jeton de session de l'utilisateur connecté : le backend
 * identifie l'utilisateur à partir de ce jeton, jamais à partir d'un userId envoyé par le navigateur.
 */

import { isDevelopmentMode } from '../utils/supabase/client';

// Valeur vide (REACT_APP_PAYMENT_API_URL=) : même adresse que le site, /api étant dirigé vers le serveur
// de paiement par le tunnel Cloudflare (voir docs/PAIEMENTS-CLOUDFLARE.md)
export const PAYMENT_API_URL = process.env.REACT_APP_PAYMENT_API_URL ?? 'http://localhost:3001';

// Session courante, synchronisée par SupabaseAuthProvider
let currentSession = null;

export const setApiSession = (session) => {
  currentSession = session || null;
};

export const getApiSession = () => currentSession;

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Erreur réseau (backend éteint ou injoignable), à distinguer d'un refus du backend.
 */
export class BackendUnreachableError extends Error {
  constructor() {
    super('Le serveur de paiement est injoignable. Réessayez dans quelques instants.');
    this.name = 'BackendUnreachableError';
  }
}

const authHeaders = () => {
  const token = currentSession?.access_token;
  if (!token) return {};

  const headers = { Authorization: `Bearer ${token}` };
  // En démonstration, le backend ne peut pas vérifier le jeton simulé : il reçoit l'identifiant démo
  if (isDevelopmentMode && currentSession?.user?.id) {
    headers['X-Demo-User-Id'] = currentSession.user.id;
  }
  return headers;
};

/**
 * Appel JSON authentifié au backend de paiement.
 * @throws {ApiError} si le backend répond avec une erreur
 * @throws {BackendUnreachableError} si le backend est injoignable
 */
export async function apiRequest(path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${PAYMENT_API_URL}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...authHeaders()
      },
      body: body !== undefined ? JSON.stringify(body) : undefined
    });
  } catch (networkError) {
    throw new BackendUnreachableError();
  }

  let result = {};
  try {
    result = await response.json();
  } catch (parseError) {
    // Réponse sans JSON : on garde l'objet vide et on s'appuie sur le statut HTTP
  }

  if (!response.ok) {
    const message = response.status === 401
      ? 'Veuillez vous connecter pour effectuer un paiement'
      : result.error || 'Erreur lors de la communication avec le serveur de paiement';
    throw new ApiError(message, response.status);
  }

  return result;
}

/**
 * Téléchargement authentifié d'un fichier (ex. reçu PDF) : enregistré sous `filename` par le navigateur.
 * @throws {ApiError|BackendUnreachableError}
 */
export async function apiDownload(path, filename) {
  let response;
  try {
    response = await fetch(`${PAYMENT_API_URL}${path}`, { headers: authHeaders() });
  } catch (networkError) {
    throw new BackendUnreachableError();
  }
  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    throw new ApiError(result.error || 'Téléchargement impossible pour le moment', response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
